/**
 * chat.js — the assistant chat surface (powered by an agent server-side).
 *
 * Two distinct modes share this widget:
 *  • HOME (welcome screen): one PERSISTENT, user-scoped thread (session 'home').
 *    It helps with anything, sees a digest of all projects, and learns the user.
 *    Its history is preloaded (newest page + "load earlier"). It never opens a
 *    plan — clicking a past project navigates to that plan instead.
 *  • PROJECT (over an open plan): a per-project thread keyed on the document id;
 *    the agent works the plan with its tools and persists that transcript.
 */

import * as api from './api.js'

let _root = null
let _logEl = null
let _inputEl = null
let _sendBtn = null
let _titleEl = null
let _newBtn = null
let _clearBtn = null
let _collapseBtn = null
let _controlsEl = null
let _recentsEl = null
let _recentsListEl = null
let _dockEl = null
let _stageEl = null
let _greetingEl = null

let _session = null        // active session id ('home' or a project/doc id)
let _project = null        // active project id (null for the home assistant)
// In-flight turns keyed by their launching session id: { userEl, replyEl, abort }.
// Per-session so navigating between the home chat and a plan chat while one is
// streaming never locks the other or strands the streaming reply.
const _liveTurns = new Map()
let _histOffset = null     // pagination cursor for "load more" (older entries)
let _histHasMore = false

const HOME_SESSION = 'home'   // the persistent, user-scoped welcome-screen chat
const _processing = new Set() // project ids whose plan is still preprocessing

// main.js registers a provider that reports where the user is right now (active
// page + view). Sent with each PROJECT turn so the agent knows the user's
// context without being told. Kept as a callback so chat.js needn't import the
// viewer/tab state (and avoids an import cycle with main.js).
let _contextProvider = null
export function setChatContextProvider(fn) { _contextProvider = fn }

// Injected by main.js so the chat can hyperlink known callouts (open their
// contact-sheet tab) and page references (navigate the main viewer).
// getCallouts() -> [{text, pages, count}]; openCallout(c); goToPage(pageNum1Based).
let _getCallouts = null
let _openCallout = null
let _goToPage = null
export function setCalloutHandler(getCallouts, openCallout, goToPage) {
  _getCallouts = getCallouts
  _openCallout = openCallout
  _goToPage = goToPage || null
}

// Render assistant text with REAL tables + live callout links. XSS-safe: every
// piece of model text goes through textContent; only our own <table>/<a> nodes
// are constructed. Markdown tables become styled HTML tables; everything else
// stays plain text (with callouts linkified).
function _renderRichInto(el, text) {
  el.textContent = ''
  const lines = String(text ?? '').split('\n')
  let i = 0
  let para = []
  const flushPara = () => {
    if (!para.length) return
    const p = document.createElement('div')
    p.className = 'chat-para'
    _linkifyInto(p, para.join('\n'))
    el.appendChild(p)
    para = []
  }
  while (i < lines.length) {
    if (_isTableHeader(lines, i)) {
      flushPara()
      const [tableEl, next] = _buildTable(lines, i)
      el.appendChild(tableEl)
      i = next
      continue
    }
    para.push(lines[i])
    i++
  }
  flushPara()
  if (!el.childNodes.length) el.textContent = text ?? ''
}

// Live-stream render for the ANSWER region: render EXACTLY like the final rich
// render (real tables + callout/page links) so nothing "reorganizes" at
// completion -- tables and links appear live as they stream. Only a half-
// streamed reference token at the very tail is stripped so the reader never
// sees raw [[PVREF:...]] markup before it resolves to a link.
//
// Throttled: the rich render rebuilds the whole answer DOM, so doing it on
// EVERY token pegs the main thread and starves the SSE reader on long answers
// (the stream then drops). Coalesce to at most one render per _STREAM_RENDER_MS.
let _streamRenderTimer = null
let _streamRenderPending = null
let _streamLastRender = 0
const _STREAM_RENDER_MS = 100

function _renderStreamingInto(el, text) {
  const cleaned = String(text ?? '').replace(/\[\[PVREF:[^\]\n]*$/, '')
  _streamRenderPending = { el, text: cleaned }
  const wait = _STREAM_RENDER_MS - (Date.now() - _streamLastRender)
  if (wait <= 0) { _flushStreamRender(); return }
  if (!_streamRenderTimer) _streamRenderTimer = setTimeout(_flushStreamRender, wait)
}

function _flushStreamRender() {
  if (_streamRenderTimer) { clearTimeout(_streamRenderTimer); _streamRenderTimer = null }
  const pending = _streamRenderPending
  _streamRenderPending = null
  if (!pending) return
  _streamLastRender = Date.now()
  _renderRichInto(pending.el, pending.text)
}

// Cancel a pending streamed render so a trailing timer never clobbers the
// final render (which may carry the authoritative answer, not just `streamed`).
function _cancelStreamRender() {
  if (_streamRenderTimer) { clearTimeout(_streamRenderTimer); _streamRenderTimer = null }
  _streamRenderPending = null
}

// A table region begins if the header+separator are confirmed, or a pipe line is
// followed by an in-progress separator, or a header-like pipe line is the tail
// still being streamed.
function _isTableRegionStart(lines, i) {
  if (_isTableHeader(lines, i)) return true
  const cur = lines[i] || ''
  if (!cur.includes('|')) return false
  const next = lines[i + 1]
  if (next != null && /-/.test(next) && /^[\s|:\-]+$/.test(next) && next.includes('|')) return true
  if (i === lines.length - 1 && _splitRow(cur).length >= 2) return true
  return false
}

function _isTableRowish(line) {
  const s = String(line || '')
  if (!s.includes('|')) return false
  if (/^[\s|:\-]+$/.test(s)) return true            // separator row
  return _splitRow(s).length >= 2
}

function _isTableHeader(lines, i) {
  const h = lines[i] || ''
  const sep = lines[i + 1] || ''
  if (!h.includes('|')) return false
  // separator row: only pipes, dashes, colons, spaces, and at least one dash run
  return /-{2,}/.test(sep) && /^[\s|:\-]+$/.test(sep) && sep.includes('|')
}

function _splitRow(line) {
  let s = String(line || '').trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  // A PVREF citation contains a literal '|' ("...:17|page 17]]"); shield it so
  // the cell split doesn't tear the citation apart and shift every column. Also
  // honour a markdown-escaped '\|' as a literal pipe inside a cell.
  s = s.replace(/\[\[PVREF:[^\]]*\]\]/g, m => m.replace(/\|/g, '\u0001'))
       .replace(/\\\|/g, '\u0001')
  return s.split('|').map(c => c.replace(/\u0001/g, '|').trim())
}

function _buildTable(lines, i) {
  const header = _splitRow(lines[i])
  let j = i + 2 // skip header + separator
  const rows = []
  while (j < lines.length && (lines[j] || '').includes('|') && !/^[\s|:\-]+$/.test(lines[j])) {
    rows.push(_splitRow(lines[j]))
    j++
  }
  const table = document.createElement('table')
  table.className = 'chat-table'
  const thead = document.createElement('thead')
  const htr = document.createElement('tr')
  for (const h of header) {
    const th = document.createElement('th')
    _linkifyInto(th, h)
    htr.appendChild(th)
  }
  thead.appendChild(htr)
  table.appendChild(thead)
  const tbody = document.createElement('tbody')
  for (const r of rows) {
    const tr = document.createElement('tr')
    for (let c = 0; c < header.length; c++) {
      const td = document.createElement('td')
      _linkifyInto(td, r[c] ?? '')
      tr.appendChild(td)
    }
    tbody.appendChild(tr)
  }
  table.appendChild(tbody)
  const wrap = document.createElement('div')
  wrap.className = 'chat-table-wrap'
  wrap.appendChild(table)
  // "Create live table" launcher: promote this rendered table to an editable,
  // saveable LIVE table (the kickoff to the whole live-table feature).
  const outer = document.createElement('div')
  outer.appendChild(wrap)
  const launch = document.createElement('div')
  launch.className = 'chat-lt-launch'
  const lb = document.createElement('button')
  lb.type = 'button'
  lb.textContent = '\u270e Create live table'
  lb.addEventListener('click', () => {
    outer.replaceWith(_makeLiveTable(header.slice(), rows.map(r => r.slice()), {}))
  })
  launch.appendChild(lb)
  outer.appendChild(launch)
  return [outer, j]
}

// ── Live (editable, saveable, page-referencing) chat tables ─────────────────
// The model's chat tables become REAL tables the user edits in place and saves
// into the project's saved-tables store (same place as Pull-Table Excel exports,
// listed bottom-left and read by the model via read_saved_table).

let _ltStyleInjected = false
function _ensureLiveTableStyle() {
  if (_ltStyleInjected) return
  _ltStyleInjected = true
  const s = document.createElement('style')
  s.textContent = `
  .chat-lt-launch { margin:4px 0 2px; }
  .chat-lt-launch button, .chat-lt-bar button {
    font-size:12px; padding:3px 9px; border-radius:6px; cursor:pointer;
    border:1px solid rgba(255,255,255,0.22); background:rgba(255,255,255,0.07); color:inherit; }
  .chat-lt-launch button:hover, .chat-lt-bar button:hover { background:rgba(255,255,255,0.16); }
  .chat-livetable { border:1px solid rgba(255,255,255,0.18); border-radius:8px; margin:8px 0; overflow:hidden; }
  .chat-lt-bar { display:flex; align-items:center; gap:6px; padding:6px 8px; flex-wrap:wrap; background:rgba(255,255,255,0.05); }
  .chat-lt-name { flex:1; min-width:120px; font-weight:600; padding:3px 6px; border-radius:5px; color:inherit;
    background:rgba(0,0,0,0.18); border:1px solid transparent; }
  .chat-lt-name:focus { outline:none; border-color:#2d6cdf; }
  .chat-lt-bar .lt-save { background:#2d6cdf; border-color:#2d6cdf; color:#fff; font-weight:600; }
  .chat-lt-status { font-size:11px; opacity:0.75; margin-left:2px; }
  .chat-lt-scroll { overflow:auto; max-width:100%; }
  .chat-livetable table { border-collapse:collapse; width:100%; }
  .chat-livetable th, .chat-livetable td { border:1px solid rgba(255,255,255,0.16); padding:3px 6px; min-width:46px; vertical-align:top; }
  .chat-livetable th { background:rgba(255,255,255,0.07); }
  .chat-livetable th [contenteditable], .chat-livetable td[contenteditable] { min-width:40px; }
  .chat-livetable [contenteditable]:focus { outline:2px solid #2d6cdf; border-radius:2px; }
  .chat-livetable .lt-del { cursor:pointer; opacity:0.45; font-size:11px; padding:0 4px; user-select:none; }
  .chat-livetable .lt-del:hover { opacity:1; color:#ff6b6b; }
  `
  document.head.appendChild(s)
}

function _ltBtn(label, fn, cls) {
  const b = document.createElement('button')
  b.type = 'button'
  b.textContent = label
  if (cls) b.className = cls
  b.addEventListener('click', fn)
  return b
}

// Strip page-ref tokens to a clean "page N" for Excel/clipboard cells.
function _ltCellPlain(t) {
  return String(t ?? '').replace(/\[\[PVREF:[^\]]*\|page (\d+)\]\]/g, 'page $1').trim()
}

// Build the editable live-table DOM from columns + rows. opts: { name, exportId }.
function _makeLiveTable(columns, rows, opts = {}) {
  _ensureLiveTableStyle()
  let cols = (columns && columns.length ? columns : ['Column 1']).map(c => String(c ?? ''))
  let data = (rows || []).map(r => cols.map((_, c) => String((r && r[c]) ?? '')))
  let exportId = opts.exportId || null
  let focusCell = null

  const wrap = document.createElement('div')
  wrap.className = 'chat-livetable'

  const bar = document.createElement('div')
  bar.className = 'chat-lt-bar'
  const nameEl = document.createElement('input')
  nameEl.className = 'chat-lt-name'
  nameEl.placeholder = 'Table name'
  nameEl.value = opts.name || 'Chat table'
  const status = document.createElement('span')
  status.className = 'chat-lt-status'

  const scroll = document.createElement('div')
  scroll.className = 'chat-lt-scroll'

  const render = () => {
    scroll.innerHTML = ''
    const table = document.createElement('table')
    const thead = document.createElement('thead')
    const htr = document.createElement('tr')
    cols.forEach((cval, c) => {
      const th = document.createElement('th')
      const span = document.createElement('span')
      span.contentEditable = 'true'
      span.textContent = cval
      span.addEventListener('input', () => { cols[c] = span.textContent })
      const del = document.createElement('span')
      del.className = 'lt-del'; del.textContent = '\u00d7'; del.title = 'Delete column'
      del.addEventListener('click', () => {
        if (cols.length > 1) { cols.splice(c, 1); data.forEach(r => r.splice(c, 1)); render() }
      })
      th.append(span, document.createTextNode(' '), del)
      htr.appendChild(th)
    })
    const hx = document.createElement('th'); hx.textContent = ''
    htr.appendChild(hx)
    thead.appendChild(htr); table.appendChild(thead)
    const tbody = document.createElement('tbody')
    data.forEach((row, r) => {
      const tr = document.createElement('tr')
      cols.forEach((_, c) => {
        const td = document.createElement('td')
        td.contentEditable = 'true'
        td.textContent = String(row[c] ?? '')
        td.addEventListener('input', () => { data[r][c] = td.textContent })
        td.addEventListener('focus', () => { focusCell = { r, c } })
        tr.appendChild(td)
      })
      const delTd = document.createElement('td')
      delTd.className = 'lt-del'; delTd.textContent = '\u00d7'; delTd.title = 'Delete row'
      delTd.addEventListener('click', () => { data.splice(r, 1); render() })
      tr.appendChild(delTd)
      tbody.appendChild(tr)
    })
    table.appendChild(tbody)
    scroll.appendChild(table)
  }

  const addColumn = () => { cols.push('Column ' + (cols.length + 1)); data.forEach(r => r.push('')); render() }
  const addRow = () => { data.push(cols.map(() => '')); render() }

  const insertPageRef = () => {
    let cur = null
    try { cur = _contextProvider && _contextProvider()?.page } catch { cur = null }
    const ans = window.prompt('Reference which page number?', cur ? String(cur) : '')
    if (!ans) return
    const p = parseInt(ans, 10)
    if (!(p > 0)) return
    const token = _project ? `[[PVREF:plan:${_project}:${p}|page ${p}]]` : `page ${p}`
    if (!focusCell) { status.textContent = 'Click a cell first, then add the page ref.'; return }
    const { r, c } = focusCell
    data[r][c] = (data[r][c] ? data[r][c] + ' ' : '') + token
    render()
    status.textContent = `Added page ${p} reference.`
  }

  const copyExcel = () => {
    const tsv = [cols, ...data].map(row => row.map(_ltCellPlain).join('\t')).join('\r\n')
    const done = () => { status.textContent = 'Copied \u2014 paste into Excel.' }
    const fallback = () => {
      const ta = document.createElement('textarea')
      ta.value = tsv; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy'); done() } catch { status.textContent = 'Copy failed.' }
      ta.remove()
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(tsv).then(done).catch(fallback)
    } else fallback()
  }

  const save = async () => {
    if (!_project) { status.textContent = 'Open a plan to save tables.'; return }
    status.textContent = 'Saving\u2026'
    try {
      const res = await api.saveLiveTable(_project, {
        export_id: exportId || undefined,
        name: nameEl.value || 'Chat table',
        columns: cols,
        rows: data,
      })
      exportId = res.export_id
      status.textContent = 'Saved to your tables \u2713'
      if (_onSavedTablesChanged) { try { _onSavedTablesChanged(_project) } catch {} }
    } catch (e) {
      status.textContent = 'Save failed: ' + (e?.message || 'error')
    }
  }

  bar.append(
    nameEl,
    _ltBtn('+ Column', addColumn),
    _ltBtn('+ Row', addRow),
    _ltBtn('\u29c9 Page ref', insertPageRef),
    _ltBtn('\u2398 Copy for Excel', copyExcel),
    _ltBtn('Save', save, 'lt-save'),
    status,
  )
  wrap.append(bar, scroll)
  render()
  return wrap
}

// main.js sets this so a Save refreshes the bottom-left saved-tables panel.
let _onSavedTablesChanged = null
export function setSavedTablesRefresh(fn) { _onSavedTablesChanged = fn }

// Called by main.js when a saved table is clicked bottom-left: re-open it in the
// chat log as a live, editable table.
export function openLiveTable(payload) {
  if (!_logEl) return
  const el = _makeLiveTable(payload?.columns || [], payload?.rows || [], {
    name: payload?.name, exportId: payload?.export_id,
  })
  const holder = document.createElement('div')
  holder.className = 'chat-msg role-assistant'
  holder.appendChild(el)
  _logEl.appendChild(holder)
  _showDock()
  _logEl.scrollTop = _logEl.scrollHeight
}


// Callout matching signature: strip all separators so "D-12" == "D 12" == "D12".
const _sig = s => String(s || '').toLowerCase().replace(/[\s._\-]+/g, '')

// The callout matchers (signature map + alternation regex source) are derived
// from the whole callout list, so building them per text-node per render is
// expensive on long streamed answers. Memoize on the callouts array identity
// (stable while a plan is open) so it's built once, not once per cell per token.
let _calloutCacheKey = null
let _calloutCache = null
function _calloutMatchers(callouts) {
  if (callouts === _calloutCacheKey && _calloutCache) return _calloutCache
  const bySig = new Map()
  const patterns = []
  const seenPat = new Set()
  for (const c of callouts) {
    if (!c) continue
    for (const form of [c.key, c.text]) {
      const s = _sig(form)
      if (!s) continue
      if (!bySig.has(s)) bySig.set(s, c)
      // Build the match pattern from the signature's letter/digit runs joined by
      // an optional separator, so a "D-12" callout also catches "D 12"/"D12".
      // Signature is [a-z0-9] only, so no regex-escaping is needed.
      if (seenPat.has(s)) continue
      seenPat.add(s)
      const parts = s.match(/[a-z]+|[0-9]+/g) || []
      if (parts.length) patterns.push(parts.join('[\\s._\\-]*'))
    }
  }
  // Longest signature first so multi-part codes win over their prefixes.
  patterns.sort((a, b) => b.length - a.length)
  _calloutCacheKey = callouts
  _calloutCache = { bySig, calloutAlt: patterns.length ? patterns.join('|') : null }
  return _calloutCache
}

// Wrap known callout tokens (open their pages tab) and page references (navigate
// the main viewer to that page) with clickable links; other text is verbatim.
function _linkifyInto(node, text) {
  text = String(text ?? '')
  if (!text) return
  const callouts = (_getCallouts && _getCallouts()) || []
  // Callouts are compact codes shaped LETTERS[sep]DIGITS (e.g. "D-12", "D 12",
  // "D12", "CR-2A"). The panel stores each callout's RAW text (whatever the OCR
  // node was) AND a normalized key ("D-12"); the model may write ANY separator
  // variant inside a table. Match a separator-free signature so every variant of
  // the SAME callout the "By Callout" panel shows gets linkified -- callouts kept
  // getting missed because we only matched the raw text with a rigid space.
  const { bySig, calloutAlt } = _calloutMatchers(callouts)
  // Only source-qualified references are navigable. Bare page numbers have no
  // document identity and must never point into whichever PDF happens to be open.
  const refAlt = '\\[\\[PVREF:(?:plan:[A-Za-z0-9_-]+|attachment:[A-Za-z0-9_-]+:[A-Za-z0-9_-]+|spec:[A-Za-z0-9_-]+):[1-9]\\d{0,5}\\|page [1-9]\\d{0,5}\\]\\]'
  const parts = []
  if (calloutAlt) parts.push('(' + calloutAlt + ')')
  parts.push('(' + refAlt + ')')
  if (!parts.length) { node.appendChild(document.createTextNode(text)); return }
  const re = new RegExp(parts.join('|'), 'gi')
  let last = 0, m
  while ((m = re.exec(text)) !== null) {
    const whole = m[0]
    const start = m.index, end = start + whole.length
    const isCallout = !!(calloutAlt && m[1] != null)
    if (isCallout) {
      const before = start > 0 ? text[start - 1] : ' '
      const after = end < text.length ? text[end] : ' '
      if (/[\w-]/.test(before) || /[\w-]/.test(after)) continue   // not a standalone token
    }
    if (start > last) node.appendChild(document.createTextNode(text.slice(last, start)))
    if (isCallout) {
      const co = bySig.get(_sig(whole))
      const a = document.createElement('a')
      a.className = 'chat-callout-link'
      a.textContent = whole
      a.title = `Open all pages referencing ${whole}`
      a.addEventListener('click', (ev) => { ev.preventDefault(); if (_openCallout && co) _openCallout(co) })
      node.appendChild(a)
    } else {
      _appendPageRefInto(node, whole)
    }
    last = end
  }
  if (last < text.length) node.appendChild(document.createTextNode(text.slice(last)))
}

// The source ID and PDF page travel with the text through chat history.
function _appendPageRefInto(node, s) {
  const m = /^\[\[PVREF:(plan|attachment|spec):([A-Za-z0-9_-]+)(?::([A-Za-z0-9_-]+))?:([1-9]\d{0,5})\|page ([1-9]\d{0,5})\]\]$/.exec(s)
  if (!m) { node.appendChild(document.createTextNode(s)); return }
  const [, kind, firstId, secondId, page, labelPage] = m
  const validShape = kind === 'attachment' ? !!secondId : !secondId
  if (!validShape || page !== labelPage) { node.appendChild(document.createTextNode(`page ${labelPage}`)); return }
  const ref = { kind, projectId: kind === 'attachment' ? firstId : (kind === 'plan' ? firstId : null),
    id: kind === 'attachment' ? secondId : firstId, page: Number(page) }
  const a = document.createElement('a')
  a.className = 'chat-page-link'
  a.textContent = `page ${page}`
  a.title = `Open ${kind} page ${page}`
  a.addEventListener('click', ev => { ev.preventDefault(); _goToPage?.(ref) })
  node.appendChild(a)
}

export function initChat() {
  if (_root) return
  const container = document.getElementById('chat-overlay')
  if (!container) return  // no chat surface on this page
  _build(container)
  openHome()              // welcome lands in the persistent home assistant
  _loadRecents()          // past project chats float below (they open the plan)
  _refreshAvailability()
}

function _build(container) {
  _root = document.createElement('div')
  _root.id = 'chat-widget'
  _root.innerHTML = `
    <div id="chat-panel" role="region" aria-label="Assistant chat">
      <div id="chat-stage">
        <div id="chat-greeting"></div>
        <div id="chat-recents" hidden>
          <div id="chat-recents-label">Your project chats</div>
          <div id="chat-recents-list"></div>
        </div>
      </div>
      <div id="chat-dock" hidden>
        <div id="chat-dock-handle" title="Drag to resize"></div>
        <div id="chat-log" aria-live="polite"></div>
      </div>
      <div id="chat-controls" hidden>
        <span id="chat-title"></span>
        <span class="chat-dock-actions">
          <button id="chat-new" hidden>New chat</button>
          <button id="chat-clear" title="Clear this conversation and reset the agent's memory of it">Clear</button>
          <button id="chat-collapse" aria-label="Hide chat" title="Hide chat">▾</button>
        </span>
      </div>
      <button type="button" id="chat-open-home" title="Open conversation" aria-label="Open conversation">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
        <span>Show conversation</span>
      </button>
      <form id="chat-form">
        <textarea id="chat-input" rows="1" placeholder="Ask about your plans…" autocomplete="off"></textarea>
        <button id="chat-send" type="submit" aria-label="Send message" title="Send">
          <svg class="ic-send" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          <svg class="ic-stop" viewBox="0 0 24 24" width="16" height="16"><rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor"/></svg>
        </button>
      </form>
    </div>`
  container.appendChild(_root)

  _stageEl = _root.querySelector('#chat-stage')
  _greetingEl = _root.querySelector('#chat-greeting')
  _dockEl = _root.querySelector('#chat-dock')
  _logEl = _root.querySelector('#chat-log')
  _inputEl = _root.querySelector('#chat-input')
  _sendBtn = _root.querySelector('#chat-send')
  _titleEl = _root.querySelector('#chat-title')
  _newBtn = _root.querySelector('#chat-new')
  _clearBtn = _root.querySelector('#chat-clear')
  _collapseBtn = _root.querySelector('#chat-collapse')
  _controlsEl = _root.querySelector('#chat-controls')
  _recentsEl = _root.querySelector('#chat-recents')
  _recentsListEl = _root.querySelector('#chat-recents-list')

  // Conversation-dock height ≈ 3 inches by default, remembered per browser.
  let h = 288
  try { const s = parseInt(localStorage.getItem('pv-chat-dock-h'), 10); if (s > 0) h = s } catch {}
  _dockEl.style.height = `${h}px`

  _newBtn.addEventListener('click', () => openHome())
  _clearBtn.addEventListener('click', () => _clearFeed())
  _collapseBtn.addEventListener('click', () => {
    // Project chat: collapse hides the transcript, input bar stays. Home chat:
    // there is no "collapsed" state — closing returns to the greeting landing
    // (never a blank centre), and sending re-opens the conversation.
    if (_project) _toggleCollapse()
    else _closeHomeToLanding()
  })
  // Home landing: an explicit arrow to re-open the existing conversation.
  _root.querySelector('#chat-open-home')?.addEventListener('click', () => {
    _showDock(); _expandIfCollapsed(); _inputEl?.focus(); _logEl.scrollTop = _logEl.scrollHeight
  })
  _initResize(_root.querySelector('#chat-dock-handle'))
  _root.querySelector('#chat-form').addEventListener('submit', (e) => { e.preventDefault(); _isBusy() ? _stop() : _submit() })
  _inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!_isBusy()) _submit() }
  })
  _inputEl.addEventListener('input', _autogrow)
  // A plan opens with only this input bar (minimized); clicking into it opens
  // the conversation so the user can see history and keep chatting.
  _inputEl.addEventListener('focus', () => {
    if (_project && _root.classList.contains('chat-collapsed')) { _showDock(); _expandIfCollapsed() }
  })
}

async function _refreshAvailability() {
  try {
    const { configured } = await api.chatStatus()
    if (!configured) {
      _sendBtn.disabled = true
      _inputEl.placeholder = 'Agent is offline (not configured)'
    }
  } catch {
    // A real send will surface the error clearly.
  }
}

// Clear = wipe the transcript from this user's view AND reset the agent's
// memory of the conversation (the box starts a fresh session). Destructive, so
// it always asks for confirmation first.
async function _clearFeed() {
  const session = _session
  const isHome = session === HOME_SESSION
  const warning = isHome
    ? 'Clear this conversation?\n\nThis removes it from your view AND makes the assistant forget everything discussed here. It can\u2019t be undone.'
    : 'Clear this conversation?\n\nThis removes it from your view AND makes the plan agent forget everything discussed here (it will re-read the plan fresh next time). It can\u2019t be undone.'
  if (!window.confirm(warning)) return
  try { await api.clearChat(session) } catch {}
  _logEl.innerHTML = ''
  _histOffset = null
  _histHasMore = false
  if (isHome) {
    _closeDock()
    _renderGreeting()
  } else {
    _addMessage('assistant', 'Conversation cleared. The plan agent has forgotten everything discussed here and will read the plan fresh.')
      .classList.add('chat-thinking')
  }
  _inputEl.focus()
}

// Collapse hides the transcript; the input bar + controls always stay put.
function _toggleCollapse() {
  if (!_root) return
  const collapsed = _root.classList.toggle('chat-collapsed')
  if (_collapseBtn) {
    _collapseBtn.textContent = collapsed ? '\u25b4' : '\u25be'
    _collapseBtn.title = collapsed ? 'Show chat' : 'Hide chat'
  }
  if (!collapsed) { _logEl.scrollTop = _logEl.scrollHeight; _inputEl?.focus() }
}

// Home chat "close": drop back to the greeting landing (greeting + drop banner +
// input) instead of collapsing to a blank centre. Re-opens when the user sends.
function _closeHomeToLanding() {
  if (!_root) return
  _root.classList.remove('chat-collapsed')
  _closeDock()
  _renderGreeting()
  _greetingEl.hidden = false
  document.body.classList.remove('doc-active')
  _syncHomeOpenArrow()
  _inputEl?.focus()
}

// The "Show conversation" arrow on the home landing is only useful when there's
// an existing transcript to re-open.
function _syncHomeOpenArrow() {
  if (!_root) return
  _root.classList.toggle('has-home-history', !!_logEl.querySelector('.chat-msg'))
}

function _expandIfCollapsed() {
  if (_root && _root.classList.contains('chat-collapsed')) _toggleCollapse()
}

// HOME = boxless, always-visible column (welcome). PROJECT = resizable dock
// over the open plan. Only the layout differs; the transcript logic is shared.
function _setMode(mode) {
  if (!_root) return
  const home = mode === 'home'
  _root.classList.toggle('chat-mode-home', home)
  _root.classList.toggle('chat-mode-project', !home)
  // Never inherit a collapsed state across navigation -- each chat opens open.
  _root.classList.remove('chat-collapsed')
  if (_collapseBtn) { _collapseBtn.textContent = '\u25be'; _collapseBtn.title = 'Hide chat' }
  if (home) {
    _dockEl.style.height = ''   // let CSS flex fill the column; no fixed box
  } else {
    let h = 288
    try { const s = parseInt(localStorage.getItem('pv-chat-dock-h'), 10); if (s > 0) h = s } catch {}
    _dockEl.style.height = `${h}px`
  }
}

/**
 * The plan-chat contract: the agent can't be spoken to until its plan has
 * finished preprocessing (CatBoost light-graph + YOLO structure). main.js
 * flips this per project id as _runPreprocessing starts and finishes.
 */
export function setProjectProcessing(projectId, processing) {
  if (!projectId) return
  if (processing) _processing.add(projectId)
  else _processing.delete(projectId)
  if (_project === projectId) _applyComposerState()
}

function _isLocked() {
  return _project != null && _processing.has(_project)
}

// A turn is "busy" only for the session that launched it -- a plan turn must
// never lock the home composer (or vice versa).
function _isBusy() { return _liveTurns.has(_session) }

// Toggle the send/Stop button for the CURRENTLY viewed session.
function _applyStreamingUI() {
  if (!_root) return
  const busy = _isBusy()
  _root.classList.toggle('chat-streaming', busy)
  if (_sendBtn) _sendBtn.title = busy ? 'Stop' : 'Send'
}

// When returning to a session whose turn is still streaming, re-attach its live
// user+reply nodes (a navigation wiped them from the log but the stream keeps
// writing to the same node refs) so the in-flight answer stays visible.
function _reattachLiveTurn(session) {
  const t = _liveTurns.get(session)
  if (!t) return
  _showDock()
  _logEl.appendChild(t.userEl)
  _logEl.appendChild(t.replyEl)
  _logEl.scrollTop = _logEl.scrollHeight
}

function _applyComposerState() {
  if (!_inputEl) return
  if (_isLocked()) {
    _root.classList.add('chat-locked')
    _inputEl.disabled = true
    _sendBtn.disabled = true
    _inputEl.placeholder = 'Reading the plan (structure + callouts)\u2026 you can chat once it\u2019s ready.'
  } else if (_isBusy()) {
    _root.classList.remove('chat-locked')
    _inputEl.disabled = true       // no new message mid-turn on THIS session
    _sendBtn.disabled = false       // but the Stop button stays clickable
  } else {
    _root.classList.remove('chat-locked')
    _inputEl.disabled = false
    _sendBtn.disabled = false
    _inputEl.placeholder = 'Ask about your plans\u2026'
  }
}

/**
 * The persistent home assistant — the welcome screen's default chat. One
 * durable, user-scoped thread (session id 'home'): its history is preloaded
 * (newest page, with "load earlier"), and it never opens a plan.
 */
export async function openHome() {
  if (!_root) return
  _session = HOME_SESSION
  _project = null
  _setMode('home')
  _titleEl.textContent = ''
  _newBtn.hidden = true
  _markActiveRecent(null)
  _histOffset = null
  _histHasMore = false
  _logEl.innerHTML = ''
  try {
    const { entries, offset, has_more } = await api.chatHistory(HOME_SESSION, { limit: 40 })
    if (entries && entries.length) {
      _histOffset = offset
      _histHasMore = has_more
      for (const e of entries) _addMessage(_roleOf(e), e.text || '')
      _renderLoadMore()
    }
  } catch {
    // fall through to the closed landing
  }
  // The welcome chat NEVER auto-opens. History is loaded and ready, but the
  // dock stays closed on the landing — it opens only when the user sends a
  // message or drops a plan.
  _renderGreeting()
  _reattachLiveTurn(HOME_SESSION)
  // Guarantee the landing is actually visible: unless a reply is ACTIVELY
  // streaming, force the greeting on with the dock closed and clear any stray
  // doc-active state — so a leftover open/streaming state can never strand the
  // welcome screen on a blank centre.
  if (!_isBusy()) {
    _root.classList.remove('chat-dock-open')
    _dockEl.hidden = true
    _greetingEl.hidden = false
    document.body.classList.remove('doc-active')
  }
  _syncHomeOpenArrow()
  _applyStreamingUI()
  _applyComposerState()
  _inputEl.focus()
}

function _roleOf(entry) {
  return entry.role === 'user' ? 'user' : (entry.role === 'error' ? 'error' : 'assistant')
}

// A "Load earlier messages" button pinned to the top of the log while older
// history remains. Clicking pages older entries in above the current ones.
function _renderLoadMore() {
  let btn = _logEl.querySelector('#chat-load-more')
  if (!_histHasMore) { if (btn) btn.remove(); return }
  if (!btn) {
    btn = document.createElement('button')
    btn.id = 'chat-load-more'
    btn.className = 'chat-load-more'
    btn.textContent = 'Load earlier messages'
    btn.addEventListener('click', _loadMore)
  }
  _logEl.prepend(btn)   // keep it at the very top
}

async function _loadMore() {
  const before = _histOffset
  if (before == null) return
  const btn = _logEl.querySelector('#chat-load-more')
  if (btn) btn.textContent = 'Loading…'
  try {
    const { entries, offset, has_more } = await api.chatHistory(_session, { limit: 40, before })
    _histOffset = offset
    _histHasMore = has_more
    const frag = document.createDocumentFragment()
    for (const e of entries) {
      const el = document.createElement('div')
      el.className = `chat-msg chat-${_roleOf(e)}`
      if (_roleOf(e) === 'assistant' && e.text) _renderRichInto(el, e.text)
      else el.textContent = e.text || ''
      frag.appendChild(el)
    }
    if (btn) btn.after(frag)   // older entries go below the button, above the rest
    else _logEl.prepend(frag)
    _renderLoadMore()
  } catch {
    if (btn) btn.textContent = 'Load earlier messages'
  }
}

function _renderGreeting() {
  _greetingEl.hidden = false
  _greetingEl.innerHTML = `
    <div class="chat-greeting">
      <h2>How can I help you?</h2>
      <div class="chat-suggestions">
        <button type="button" class="chat-suggestion" data-q="What am I working on right now?">What am I working on?</button>
        <button type="button" class="chat-suggestion" data-q="Which of my jobs still need attention?">Which jobs need attention?</button>
        <button type="button" class="chat-suggestion" data-q="How do I export a plan table to Excel?">Export a table to Excel</button>
      </div>
      <button type="button" id="home-dropzone" class="home-dropzone" aria-label="Add a plan or spec: drag a file here or click to browse">
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M8 8l4-4 4 4"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>
        <span class="home-dropzone-text">
          <strong>Drag &amp; drop a plan or spec here</strong>
          <span class="home-dropzone-sub">PDF or BPX &mdash; or <span class="home-dropzone-link">browse your files</span></span>
        </span>
      </button>
    </div>`
  _greetingEl.querySelector('#home-dropzone')?.addEventListener('click', () => {
    document.getElementById('file-input')?.click()
  })
  for (const chip of _greetingEl.querySelectorAll('.chat-suggestion')) {
    chip.addEventListener('click', () => {
      _inputEl.value = chip.dataset.q || chip.textContent
      _autogrow()
      _inputEl.focus()
    })
  }
}

// The conversation lives in a resizable bottom dock; the input strip always stays.
function _showDock() {
  _greetingEl.hidden = true
  _root.classList.add('chat-dock-open')
  _dockEl.hidden = false
}

function _closeDock() {
  _root.classList.remove('chat-dock-open')
  _dockEl.hidden = true
}

function _initResize(handle) {
  let startY = 0, startH = 0
  const onMove = (e) => {
    const h = Math.max(140, Math.min(window.innerHeight * 0.75, startH + (startY - e.clientY)))
    _dockEl.style.height = `${h}px`
  }
  const onUp = () => {
    document.removeEventListener('pointermove', onMove)
    document.removeEventListener('pointerup', onUp)
    try { localStorage.setItem('pv-chat-dock-h', String(Math.round(_dockEl.getBoundingClientRect().height))) } catch {}
  }
  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault()
    startY = e.clientY
    startH = _dockEl.getBoundingClientRect().height
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  })
}

async function _loadRecents() {
  let sessions = []
  try {
    sessions = (await api.chatSessions()).sessions || []
  } catch {
    sessions = []
  }
  // Only past PROJECT chats, named by the plan's filename.
  const projects = sessions.filter((s) => s.is_project && s.has_history)
  _recentsListEl.innerHTML = ''
  if (!projects.length) {
    _recentsEl.hidden = true
    return
  }
  _recentsEl.hidden = false
  for (const s of projects) {
    const item = document.createElement('button')
    item.className = 'chat-recent'
    item.dataset.projectId = s.project_id
    item.title = s.title || s.project_id
    item.textContent = s.title || s.project_id
    // Clicking a chat is the SAME as clicking its plan — one unified selection
    // (main.js opens the plan tab AND loads this chat on the home screen).
    item.addEventListener('click', () => window.dispatchEvent(new CustomEvent(
      'planview:select-project', { detail: { docId: s.project_id, filename: s.title } })))
    _recentsListEl.appendChild(item)
  }
  _markActiveRecent(_project)
}

function _markActiveRecent(projectId) {
  if (!_recentsListEl) return
  for (const el of _recentsListEl.querySelectorAll('.chat-recent')) {
    el.classList.toggle('active', el.dataset.projectId === projectId)
  }
}

/** Reload the past-project chips (e.g. after a new plan is uploaded). */
export function refreshRecents() { _loadRecents() }

/**
 * Load a project's chat on the home screen (transcript + active project), so a
 * message uses that project and the agent retrieves its plan + saved context.
 * Called by main.js for BOTH the left plan menu and the chat chips.
 */
export async function openProjectChat(projectId, filename, reveal = true) {
  if (!_root) return
  if (_project === projectId) { if (reveal) _showDock(); return }  // already bound to this plan
  _session = projectId
  _project = projectId
  _setMode('project')
  _titleEl.textContent = filename || projectId
  _newBtn.hidden = false
  _markActiveRecent(projectId)
  _histOffset = null
  _histHasMore = false
  // reveal=false: bind the chat to this plan but show ONLY the input bar over
  // the plan. Do NOT add chat-dock-open -- that class is what kept the chatbox
  // sitting open over the plan. The conversation opens when the user clicks the
  // input (focus handler) or sends a message.
  if (reveal) {
    _showDock()
  } else {
    _closeDock()
    _root.classList.add('chat-collapsed')
  }
  _applyComposerState()
  _logEl.innerHTML = '<div class="chat-msg chat-assistant chat-thinking">Loading history…</div>'
  try {
    const { entries, offset, has_more } = await api.chatHistory(projectId, { limit: 40 })
    _logEl.innerHTML = ''
    if (!entries.length) {
      _addMessage('assistant', 'No messages yet — ask the first question about this plan.')
        .classList.add('chat-thinking')
    } else {
      _histOffset = offset
      _histHasMore = has_more
      for (const e of entries) _addMessage(_roleOf(e), e.text || '')
      _renderLoadMore()
    }
  } catch {
    _logEl.innerHTML = ''
    _addMessage('error', 'Could not load history.')
  }
  _reattachLiveTurn(projectId)
  _applyStreamingUI()
  _applyComposerState()
  _logEl.scrollTop = _logEl.scrollHeight
}

function _autogrow() {
  _inputEl.style.height = 'auto'
  _inputEl.style.height = `${Math.min(_inputEl.scrollHeight, 160)}px`
}

function _addMessage(role, text = '') {
  const el = document.createElement('div')
  el.className = `chat-msg chat-${role}`
  if (role === 'assistant' && text) _renderRichInto(el, text)
  else el.textContent = text
  _logEl.appendChild(el)
  _logEl.scrollTop = _logEl.scrollHeight
  return el
}

/**
 * A synthetic assistant message (UI-only, not persisted) shown like a real
 * reply -- opens the chat dock and scrolls it into view as the newest line.
 * Used e.g. to tell the user their just-uploaded plan is being processed.
 */
export function addAssistantNotice(text = '') {
  if (!_root) return null
  _showDock()
  _expandIfCollapsed()
  const el = _addMessage('assistant', text)
  _logEl.scrollTop = _logEl.scrollHeight
  return el
}

/**
 * Show a dropped document in the chat as a card with a first-page render and a
 * live status line. Returns a handle to update the status or make it clickable.
 */
export function addDocCard({ thumb = '', filename = '', status = '' } = {}) {
  if (!_root) return null
  _showDock()
  _expandIfCollapsed()
  const card = document.createElement('div')
  card.className = 'chat-msg chat-doccard'
  const img = document.createElement('img')
  img.className = 'doccard-thumb'
  img.alt = ''
  if (thumb) img.src = thumb
  const body = document.createElement('div')
  body.className = 'doccard-body'
  const nameEl = document.createElement('div')
  nameEl.className = 'doccard-name'
  nameEl.textContent = filename
  const statusEl = document.createElement('div')
  statusEl.className = 'doccard-status'
  statusEl.textContent = status
  body.append(nameEl, statusEl)
  card.append(img, body)
  _logEl.appendChild(card)
  _logEl.scrollTop = _logEl.scrollHeight
  return {
    el: card,
    setStatus(s) { statusEl.textContent = s },
    setThumb(src) { if (src) img.src = src },
    setClickable(fn) { card.classList.add('clickable'); card.onclick = fn },
    remove() { card.remove() },
  }
}

async function _submit() {
  if (_isBusy() || _isLocked()) return
  const message = _inputEl.value.trim()
  if (!message) return

  _showDock()                 // conversation window appears as soon as you talk
  _expandIfCollapsed()
  const userEl = _addMessage('user', message)
  _inputEl.value = ''
  _autogrow()

  // Bind this turn to the session that launched it, so navigating to another
  // chat (home <-> plan) mid-stream never locks the other or strands the reply.
  const turnSession = _session
  const turnProject = _project
  const abort = new AbortController()

  const reply = _addMessage('assistant')
  reply.classList.add('chat-thinking')
  reply.textContent = 'Thinking\u2026'
  _liveTurns.set(turnSession, { userEl, replyEl: reply, abort })
  _applyStreamingUI()
  _applyComposerState()
  let streamed = ''
  let thinking = ''
  let thinkEl = null      // persistent chain-of-thought + tool-call region
  let answerEl = null     // the streaming answer region (building-table lives here)
  const _ensureThink = () => {
    if (!thinkEl) {
      reply.textContent = ''
      thinkEl = document.createElement('div')
      thinkEl.className = 'chat-stream-think'
      reply.appendChild(thinkEl)
    }
    return thinkEl
  }
  const _ensureAnswer = () => {
    if (!answerEl) {
      answerEl = document.createElement('div')
      answerEl.className = 'chat-stream-answer'
      reply.appendChild(answerEl)
    }
    return answerEl
  }
  // Show the tail of the reasoning/tool chain so it streams smoothly AND stays
  // visible even while the answer (and its building-table) renders below it.
  const _showThinking = () => {
    _ensureThink().textContent = thinking.length > 1600 ? '\u2026' + thinking.slice(-1600) : thinking
  }

  // Tell the agent where the user is (project chats only — the home assistant
  // has no open plan). Best-effort: a missing provider just omits it.
  let ctx = null
  if (turnProject && _contextProvider) {
    try { ctx = _contextProvider() } catch { ctx = null }
  }

  try {
    const answer = await api.sendChatMessage(
      message,
      { sessionId: turnSession, projectId: turnProject, page: ctx?.page ?? null, view: ctx?.view ?? null,
        viewing: ctx?.viewing ?? null, signal: abort.signal },
      {
        onStatus: (s) => { if (!streamed && !thinking) _ensureThink().textContent = s.message || 'Working…' },
        onTool: (t) => {
          // Keep the tool-call chain live for the WHOLE turn, even after the
          // answer starts, so the building-table never hides what's happening.
          thinking += (thinking ? '\n' : '') + (t.message || 'Using a tool…') + '\n'
          _showThinking()
        },
        onToken: (t) => {
          if (t.kind === 'answer') {
            if (!streamed) reply.classList.remove('chat-thinking')
            streamed += t.text
            _renderStreamingInto(_ensureAnswer(), streamed)   // table -> shimmer; prose streams live
          } else {
            // Reasoning deltas keep flowing into the (persistent) think region.
            thinking += t.text
            _showThinking()
          }
          if (_session === turnSession) _logEl.scrollTop = _logEl.scrollHeight
        },
      },
    )
    _cancelStreamRender()
    reply.classList.remove('chat-thinking')
    _renderRichInto(reply, answer || streamed || '(no answer)')
  } catch (err) {
    _cancelStreamRender()
    reply.classList.remove('chat-thinking')
    if (err?.name === 'AbortError') {
      _renderRichInto(reply, (streamed ? streamed + '\n\n' : '') + '⏹ Stopped.')
    } else if (streamed && streamed.trim()) {
      // Stream dropped/errored mid-answer -- keep the partial takeoff that
      // already arrived instead of discarding it and showing only an error.
      _renderRichInto(reply, streamed)
      const note = document.createElement('div')
      note.className = 'chat-error'
      note.textContent = '\u26A0 Connection dropped before the answer finished \u2014 ask again to continue.'
      reply.appendChild(note)
    } else {
      reply.classList.add('chat-error')
      reply.textContent = err.message || 'Something went wrong.'
    }
  } finally {
    _liveTurns.delete(turnSession)
    // Only touch the composer/log if the user is still viewing this session; a
    // background turn that finished on another chat must not disturb it.
    if (_session === turnSession) {
      _applyStreamingUI()
      _applyComposerState()
      _inputEl.focus()
      _logEl.scrollTop = _logEl.scrollHeight
    }
  }
}

// Stop the in-flight turn — aborts the stream (the send button doubles as Stop
// while a reply is generating).
function _stop() {
  try { _liveTurns.get(_session)?.abort?.abort() } catch { /* already done */ }
}

